import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import Spinner from '@components/Spinner';
import { SubscriptionFormProps } from '@components/SubscriptionForm';
import useEventTracking from '@hooks/useEventTracking';
import { STRIPE_PUBLISH_KEY } from '@src/env';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Event, ImpressionElement, TrackingLocation } from '@tracking';
import { createSubscription } from '@utils/backend-api/stripe';
import { PAYMENT_ELEMENT_APPEARANCE } from '@utils/constants';

import PaymentMethod from './PaymentMethod';

const stripePromise = loadStripe(STRIPE_PUBLISH_KEY);
const PaymentForm = ({
  buttonText,
  presentedOfferCodes,
  referringSubscriberName,
  referringSubscriberGuid,
  selectedOffer,
  stripeCustomerId,
}: Omit<SubscriptionFormProps, 'formLocation'>) => {
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const isReferral = !!referringSubscriberName;

  useEventTracking(
    {
      eventName: Event.Impression,
      impressionElement: ImpressionElement.Payment,
      location: TrackingLocation.PaymentForm,
    },
    true
  );

  useEffect(() => {
    (async () => {
      try {
        const { clientSecret, subscriptionId } = await createSubscription({
          code: selectedOffer.code,
          stripe_customer_id: stripeCustomerId,
        });
        setClientSecret(clientSecret);
        setSubscriptionId(subscriptionId);
      } catch {
        toast.error('Something went wrong. Please refresh this page or try again later.');
      }
    })();
  }, [stripeCustomerId, selectedOffer.code]);

  return (
    <div className="mt-32">
      {clientSecret ? (
        <Elements
          key={clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: PAYMENT_ELEMENT_APPEARANCE,
          }}
        >
          <PaymentMethod
            buttonText={buttonText}
            presentedOfferCodes={presentedOfferCodes}
            isReferral={isReferral}
            referringSubscriberName={referringSubscriberName}
            referringSubscriberGuid={referringSubscriberGuid}
            selectedOffer={selectedOffer}
            subscriptionId={subscriptionId}
          />
        </Elements>
      ) : (
        <div className="my-16 text-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
